import { ButtonBase } from "@mui/material";
import { COLOR_BLACK, COLOR_GREY_BORDER } from "helpers/colors";
import { FONT_FAMILY_GRAPHIK } from "helpers/FONT";
import { Creation } from "mdi-material-ui";
import Dialog from "molecules/Dialog";
import React from "react";

// Preciser's terms for Glory League users, shown before we hand them over.
export const PRECISER_TERMS_URL = "https://www.preciser.io/terms-and-conditions-glory-league";

// Preciser's brand lime. The pill that opens this dialog wears it too.
export const COLOR_AI_ACTION = "#E2FF57";

const COLOR_CARD = "#F2F2F2";
const COLOR_CARD_CHIP = "#E2E2E2";
const COLOR_BODY_TEXT = "#4A4A4A";

const CARD_STYLE = { padding: "16px 18px", borderRadius: 8, backgroundColor: COLOR_CARD };
const CARD_TITLE_STYLE = { fontSize: "1rem", fontWeight: 500 };
const CARD_BODY_STYLE = { marginTop: "6px", fontSize: "0.875rem", lineHeight: 1.5, color: COLOR_BODY_TEXT };

const BUTTON_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "16px 20px",
  borderRadius: "8px",
  fontFamily: FONT_FAMILY_GRAPHIK,
  fontSize: "1.0625rem",
  lineHeight: 1.2,
};

const BUTTON_STYLE_PRIMARY = {
  backgroundColor: COLOR_AI_ACTION,
  color: COLOR_BLACK,
  fontWeight: 600,
  "&:hover": { backgroundColor: "#D3F02F" },
};

const BUTTON_STYLE_CANCEL = {
  color: COLOR_BLACK,
  fontWeight: 400,
  "&:hover": { backgroundColor: COLOR_CARD },
};

const TRACKED_STATS = ["Misses", "Rebounds", "Assists", "Steals", "Blocks", "Turnovers"];

// What the AI does and does not do, in the order a buyer asks it.
const NOTES = [
  {
    title: "Points & scoring",
    body: "Points are determined by the official scoring record from the scoring app. AI stats adjust highlight timing only — scoring errors must be corrected inside Glory League but do not impact final official results.",
  },
  {
    title: "Shot chart",
    body: "Shot chart data is not generated by AI stats. Shot locations can be added manually after processing is complete.",
  },
  {
    title: "Uniform visibility",
    body: "If numbers are not visible due to non-regulation FIBA uniforms or obstructions like ponytails, stats may be attributed to the team rather than an individual player and will need to be matched after processing.",
  },
];

// The order confirmation shown before we hand the user to Preciser.
export default function GameOverviewAiStatsDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog
      name="GameOverviewAiStats"
      open={open}
      onClose={onClose}
      noPadding
      // Full screen on a small screen keeps square corners and avoids a weirdly small dialog that is hard to read.
      PaperProps={{ sx: { borderRadius: "8px", "&.MuiDialog-paperFullScreen": { borderRadius: 0 } } }}
    >
      <div
        style={{
          display: "flex",
          flexFlow: "column nowrap",
          flex: "1 1 auto",
          minHeight: 0,
          fontFamily: FONT_FAMILY_GRAPHIK,
          color: COLOR_BLACK,
        }}
      >
        <div style={{ padding: "20px 20px 18px", borderBottom: `1px solid ${COLOR_GREY_BORDER}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 34,
                height: 34,
                borderRadius: 10,
                backgroundColor: COLOR_AI_ACTION,
              }}
            >
              <Creation style={{ fontSize: 20, color: COLOR_BLACK }} />
            </div>
            <div style={{ fontSize: "1.125rem" }}>
              <strong style={{ fontWeight: 600 }}>Preciser</strong> <span style={{ opacity: 0.6 }}>AI Stats</span>
            </div>
          </div>
          <h1 style={{ fontSize: "1.75rem", fontWeight: 500, lineHeight: 1.2, margin: "18px 0 0" }}>
            Order AI Stats for this game
          </h1>
        </div>

        <div
          style={{
            display: "flex",
            flexFlow: "column nowrap",
            gap: "12px",
            padding: "20px",
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <div style={CARD_STYLE}>
            <div style={CARD_TITLE_STYLE}>What&apos;s included with AI stats</div>
            <div style={CARD_BODY_STYLE}>AI-tracked stats per player &amp; team</div>
            <div style={{ display: "flex", flexFlow: "row wrap", gap: "8px", marginTop: "14px" }}>
              {TRACKED_STATS.map((stat) => (
                <span
                  key={stat}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 999,
                    backgroundColor: COLOR_CARD_CHIP,
                    fontSize: "0.875rem",
                  }}
                >
                  {stat}
                </span>
              ))}
            </div>
          </div>

          {NOTES.map(({ title, body }) => (
            <div key={title} style={CARD_STYLE}>
              <div style={CARD_TITLE_STYLE}>{title}</div>
              <div style={CARD_BODY_STYLE}>{body}</div>
            </div>
          ))}
        </div>

        <div style={{ padding: "18px 20px 20px", borderTop: `1px solid ${COLOR_GREY_BORDER}` }}>
          <div style={{ fontSize: "0.875rem", lineHeight: 1.5, color: COLOR_BODY_TEXT }}>
            You&apos;ll be taken to <strong style={{ fontWeight: 600, color: COLOR_BLACK }}>Preciser</strong> to
            complete the order. If your account has no credit, Preciser will ask you to purchase credit before
            submitting.
          </div>
          <a
            style={{ display: "inline-block", margin: "12px 12px 0 0", fontSize: "0.875rem", color: COLOR_BODY_TEXT }}
            target="_blank"
            rel="noreferrer"
            href={PRECISER_TERMS_URL}
          >
            Preciser Terms &amp; Conditions
          </a>
          <div style={{ display: "flex", alignItems: "stretch", gap: "12px", marginTop: "24px" }}>
            <ButtonBase onClick={onConfirm} sx={{ ...BUTTON_STYLE, flex: "1 1 auto", ...BUTTON_STYLE_PRIMARY }}>
              Continue to Preciser
            </ButtonBase>
            <ButtonBase onClick={onClose} sx={{ ...BUTTON_STYLE, ...BUTTON_STYLE_CANCEL }}>
              Cancel
            </ButtonBase>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
